/**
 * The materiality matrix's topic view: each ESRS topic (E1 to G1, plus the
 * entity-specific topics) material or not on each axis. A topic is material on an
 * axis when any of its matters is, which is how the DMA results table marks a
 * topic row.
 *
 * Shared by the platform's matrix (`loadMaterialityMatrixData`) and the chat
 * matrix card (`get_dma_materiality_matrix`), so the two place every topic, and
 * count the material matters, alike. In `common` so the platform's paywall
 * preview can build its mock topics with it too.
 */
import type { DmaMatrixTopic, DmaMatrixValence } from '@upright/common/mcp-payloads/dma-matrix';
import type { MaterialityStatus } from '@upright/common/mcp-payloads/types';
import {
  friendlySustainabilityMatterTitle,
  getMatterById,
  getParentTopicId,
  isRelevantMatter,
  isSustainabilityMatterLeaf,
} from '@upright/common/model/input/materiality-assessment/matters';
import {
  CompanyLevelMaterialityResult,
  MaterialityValence,
} from '@upright/common/model/input/materiality-assessment/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { MaterialityResultOfMatter } from '@upright/common/model/output/materiality/csrd-company-results/types';
import {
  CsrdTopicIdWithEsId,
  csrdTopicIdsWithEsId,
  EsTopicsId,
  esTopicsId,
} from '@upright/common/model/output/materiality/types';
import { MatrixMatter, MatrixMattersByBucket } from './types';
import { getMatterBucketId } from './utils';

/** Which sides of one matter are material, on each axis. */
export interface MatterMaterialityFlags {
  matterId: SustainabilityMatterId;
  impact: boolean;
  financial: boolean;
  /** A positive impact or an opportunity is material. */
  positive: boolean;
  /** A negative impact or a risk is material. */
  negative: boolean;
}

/** The matters the matrix is drawn from: the relevant leaf matters. */
export function getRelevantLeafResults(materialityResults: MaterialityResultOfMatter[]): MaterialityResultOfMatter[] {
  return materialityResults.filter(r => isSustainabilityMatterLeaf(r.matterId) && isRelevantMatter(r.matterId));
}

/**
 * Read the same rows as the matrix: financial effects where the matter has them,
 * financial materiality otherwise (`prepareCompanyMatrixMatters`). A legacy variant
 * has neither, and only its matter-level result says whether it is financially
 * material, with no valence.
 */
export function matterMaterialityFlags(r: MaterialityResultOfMatter, isLegacyFm: boolean): MatterMaterialityFlags {
  const financialRows = r.companyFinancialEffects.length ? r.companyFinancialEffects : r.companyFinancialMateriality;
  const isMaterialOn = (
    rows: Array<{ valence: MaterialityValence; isMaterial: boolean }>,
    valence: MaterialityValence,
  ) => rows.some(row => row.valence === valence && row.isMaterial);

  const impactPositive = isMaterialOn(r.companyImpactMateriality, MaterialityValence.POSITIVE);
  const impactNegative = isMaterialOn(r.companyImpactMateriality, MaterialityValence.NEGATIVE);
  const financialPositive = isMaterialOn(financialRows, MaterialityValence.POSITIVE);
  const financialNegative = isMaterialOn(financialRows, MaterialityValence.NEGATIVE);

  return {
    matterId: r.matterId,
    impact: impactPositive || impactNegative,
    financial:
      financialPositive || financialNegative || (isLegacyFm && r.fmResult === CompanyLevelMaterialityResult.MATERIAL),
    positive: impactPositive || financialPositive,
    negative: impactNegative || financialNegative,
  };
}

function isMaterialMatter(flags: MatterMaterialityFlags): boolean {
  return flags.impact || flags.financial;
}

/**
 * The matters counted as material: material on either axis by their own rows, or
 * placed in a cell of the matter matrix (every cell but low × low). On a legacy
 * variant a placement reads other rows, so it is added rather than assumed to
 * agree. Without the matrix (a caller who may not see the positions), the rows alone.
 */
export function collectMaterialMatterIds(
  flags: MatterMaterialityFlags[],
  matrixData: MatrixMattersByBucket | null,
): Set<SustainabilityMatterId> {
  const materialIds = new Set(flags.filter(isMaterialMatter).map(f => f.matterId));
  const lowLowBucket = getMatterBucketId('low', 'low');
  for (const [bucketId, matters] of Object.entries(matrixData ?? {})) {
    if (bucketId === lowLowBucket) continue;
    for (const matter of matters) materialIds.add(matter.matterId);
  }
  return materialIds;
}

/** A placement's valence: `groupMattersByBucket` keeps both sides only when they share the cell, and nulls the other side otherwise. */
export function getPlacementValence(matter: MatrixMatter): DmaMatrixValence {
  if (matter.positive && matter.negative) return 'positive-and-negative';
  return matter.positive ? 'positive' : 'negative';
}

export function toMatrixValence(positive: boolean, negative: boolean): DmaMatrixValence | null {
  if (positive && negative) return 'positive-and-negative';
  if (positive) return 'positive';
  if (negative) return 'negative';
  return null;
}

/** The ESRS topic a matter belongs to, or null for one outside the topic tree. */
export function getMatrixTopicOf(matterId: SustainabilityMatterId): CsrdTopicIdWithEsId | null {
  try {
    return getParentTopicId(matterId);
  } catch {
    return null;
  }
}

/** The ESRS category above a topic. The entity-specific topics have none and are their own. */
function categoryOf(topicId: CsrdTopicIdWithEsId): SustainabilityMatterId | EsTopicsId {
  if (topicId === esTopicsId) return topicId;
  return getMatterById(topicId).parent ?? topicId;
}

function materialityStatusOf(isMaterial: boolean): MaterialityStatus {
  return isMaterial ? 'material' : 'not-material';
}

/** Every topic in ESRS order, material or not. */
export function buildMatrixTopics(flags: MatterMaterialityFlags[]): DmaMatrixTopic[] {
  return csrdTopicIdsWithEsId.map(topicId => {
    const material = flags.filter(f => getMatrixTopicOf(f.matterId) === topicId && isMaterialMatter(f));
    const categoryId = categoryOf(topicId);
    return {
      topicId,
      topicName: friendlySustainabilityMatterTitle(topicId),
      categoryId,
      categoryName: friendlySustainabilityMatterTitle(categoryId),
      impactMateriality: materialityStatusOf(material.some(f => f.impact)),
      financialMateriality: materialityStatusOf(material.some(f => f.financial)),
      valence: toMatrixValence(
        material.some(f => f.positive),
        material.some(f => f.negative),
      ),
      materialMatters: material.map(f => ({
        matterId: f.matterId,
        valence: toMatrixValence(f.positive, f.negative),
        materialOn: f.impact && f.financial ? 'impact-and-financial' : f.impact ? 'impact' : 'financial',
      })),
    };
  });
}
